import jsPDF from "jspdf";
import { pdfSafe } from "./pdfText";

export interface WorksheetQuestion {
  index: number;
  topic: string;
  question: string;
}

export interface WorksheetMeta {
  difficulty: string;
  topics: string;
  date: string;
}

/**
 * Render a student-facing worksheet PDF with ElementPad branding.
 * Each question gets writing room (lines) for the answer.
 */
export function downloadWorksheetPdf(
  questions: WorksheetQuestion[],
  meta: WorksheetMeta,
) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 56;
  let y = 0;

  function header() {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("ELEMENTPAD · CHEMISTRY WORKSHEET", MARGIN, 32);
    doc.setTextColor(37, 99, 235);
    doc.text(meta.date.toUpperCase(), PAGE_W - MARGIN, 32, { align: "right" });
    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 40, PAGE_W - MARGIN, 40);
  }

  function footer(page: number, total: number) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${page} of ${total}`, PAGE_W - MARGIN, PAGE_H - 24, {
      align: "right",
    });
    doc.text(
      "elementpad.net · Generated on-device. Nothing is uploaded.",
      MARGIN,
      PAGE_H - 24,
    );
  }

  function newPage() {
    doc.addPage();
    header();
    y = 70;
  }

  function ensureSpace(needed: number) {
    if (y + needed > PAGE_H - 60) newPage();
  }

  // Cover / title
  header();
  y = 70;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(20);
  doc.text("Chemistry Practice Worksheet", MARGIN, y);
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  const metaWrap = doc.splitTextToSize(
    pdfSafe(`${questions.length} questions · Difficulty: ${meta.difficulty} · Topics: ${meta.topics}`),
    PAGE_W - 2 * MARGIN,
  );
  doc.text(metaWrap, MARGIN, y);
  y += metaWrap.length * 12 + 14;

  // Name / date / score row
  doc.setFontSize(10);
  doc.setTextColor(60);
  const labels = ["Name:", "Date:", `Score:        / ${questions.length}`];
  const colW = (PAGE_W - 2 * MARGIN) / 3;
  labels.forEach((label, i) => {
    const x = MARGIN + colW * i;
    doc.text(label, x, y);
    doc.setDrawColor(180);
    doc.line(x + 40, y + 2, x + colW - 12, y + 2);
  });
  y += 22;

  doc.setDrawColor(230);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 16;

  const ANSWER_LINES = 3;
  const LINE_GAP = 18;

  questions.forEach((q) => {
    const numLabel = `${q.index}.`;
    const qLines = doc.splitTextToSize(pdfSafe(q.question), PAGE_W - 2 * MARGIN - 24);
    const blockHeight = qLines.length * 12 + 8 + ANSWER_LINES * LINE_GAP + 10;
    ensureSpace(blockHeight);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(37, 99, 235);
    doc.text(numLabel, MARGIN, y);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(20);
    doc.text(qLines, MARGIN + 24, y);
    y += qLines.length * 12 + 10;

    doc.setDrawColor(210);
    doc.setLineWidth(0.3);
    for (let i = 0; i < ANSWER_LINES; i++) {
      doc.line(MARGIN + 24, y, PAGE_W - MARGIN, y);
      y += LINE_GAP;
    }
    y += 6;
  });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    footer(i, total);
  }

  doc.save("elementpad-worksheet.pdf");
}